import React, { useRef, useState, useEffect } from "react";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { useEditLineCrossingsStore } from "../editLineCrossingsStore/editLineCrossingsStore";
import { getNextLineColor } from "../EditLineCrossingsUtils/lineColors";
import { getNumbersAfterEditCamera } from "../../EditCamera/EditCamera";
import type { Line } from "../editLineCrossingsStore/editLineCrossingsStore";

const LARGER_MODE_SCALE = 1.5;

// Draw perpendicular crossing line with arrow
const drawArrow = (
  ctx: CanvasRenderingContext2D,
  start: [number, number],
  end: [number, number],
  direction: "AtoB" | "BtoA" | "Both",
  lineColor: string
) => {
  const centerX = (start[0] + end[0]) / 2;
  const centerY = (start[1] + end[1]) / 2;

  // Calculate the main line vector
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const lineLength = Math.sqrt(dx * dx + dy * dy);

  if (lineLength === 0) return;

  // Calculate perpendicular vector (rotate 90 degrees)
  // For a vector (dx, dy), the perpendicular is (-dy, dx) or (dy, -dx)
  // We'll use (-dy, dx) to get the left side when facing from A to B
  const perpDx = -dy / lineLength;
  const perpDy = dx / lineLength;

  // Length of the crossing line - longer
  const crossingLineLength = 50;
  const halfLength = crossingLineLength / 2;

  // Arrow properties - equilateral triangle
  const arrowLength = 12;
  // For equilateral triangle: base = 2 * height / sqrt(3)
  // Making it wider for equilateral triangle look
  const arrowWidth = (2 * arrowLength) / Math.sqrt(3); // Approximately 13.86 for equilateral

  // Use actuate-blue for the crossing arrow
  const crossingArrowColor = "#FF1744"; // Neon red

  const drawCrossingLineWithArrow = (
    sideMultiplier: number,
    fromCenter: boolean
  ) => {
    // Calculate the perpendicular offset
    const offsetX = perpDx * halfLength * sideMultiplier;
    const offsetY = perpDy * halfLength * sideMultiplier;

    // Calculate the arrow tip position (end of the crossing line)
    const arrowTipX = centerX + offsetX;
    const arrowTipY = centerY + offsetY;

    // Calculate where the line should end (arrowhead base, which is arrowLength back from tip)
    const arrowBaseOffsetX = perpDx * arrowLength * sideMultiplier;
    const arrowBaseOffsetY = perpDy * arrowLength * sideMultiplier;
    const lineEndX = arrowTipX - arrowBaseOffsetX;
    const lineEndY = arrowTipY - arrowBaseOffsetY;

    // Start point of the crossing line
    // For single direction: from opposite side (full line)
    // For "Both": from center (half line on each side)
    const crossStartX = fromCenter ? centerX : centerX - offsetX; // Opposite side
    const crossStartY = fromCenter ? centerY : centerY - offsetY; // Opposite side

    // Draw the crossing line (stops at arrowhead base)
    ctx.strokeStyle = crossingArrowColor;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(crossStartX, crossStartY);
    ctx.lineTo(lineEndX, lineEndY);
    ctx.stroke();

    // Draw arrowhead at the tip (pointing away from the main line)
    const arrowAngle = Math.atan2(offsetY, offsetX);
    ctx.save();
    ctx.translate(arrowTipX, arrowTipY);
    ctx.rotate(arrowAngle);

    ctx.fillStyle = crossingArrowColor;
    ctx.beginPath();
    ctx.moveTo(0, 0); // Arrow tip
    ctx.lineTo(-arrowLength, -arrowWidth / 2); // Base corner 1
    ctx.lineTo(-arrowLength, arrowWidth / 2); // Base corner 2
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  };

  // Draw based on direction
  // AtoB: arrow on side B (pointing from A toward B)
  // BtoA: arrow on side A (pointing from B toward A)
  // Both: draw arrows on both sides
  // Note: sideMultiplier = -1 puts arrow on side A, sideMultiplier = 1 puts arrow on side B
  if (direction === "AtoB") {
    // Full line from side A to side B arrowhead base
    drawCrossingLineWithArrow(1, false); // Side B (counter-clockwise/left) - pointing from A to B
  } else if (direction === "BtoA") {
    // Full line from side B to side A arrowhead base
    drawCrossingLineWithArrow(-1, false); // Side A (clockwise/right) - pointing from B to A
  } else if (direction === "Both") {
    // For "Both", draw two separate lines from center, each stopping at arrowhead base
    // Side A (clockwise/right)
    drawCrossingLineWithArrow(-1, true);
    // Side B (counter-clockwise/left)
    drawCrossingLineWithArrow(1, true);
  }
};

interface LineDrawerProps {
  largerMode?: boolean;
  setLargerMode?: (value: boolean) => void;
}

export const LineDrawer = ({
  largerMode = false,
  setLargerMode,
}: LineDrawerProps = {}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const {
    lines,
    setLines,
    currentLine,
    setCurrentLine,
    isDrawing,
    setIsDrawing,
    selectedLineIndex,
    setSelectedLineIndex,
    lineColor,
    dimensions,
    imageURL,
    metricLabels,
    setEditsMade,
    lineCrossingActive,
  } = useEditLineCrossingsStore();

  const imageWidth = dimensions.width;
  const imageHeight = dimensions.height;

  const [hoveredLineIndex, setHoveredLineIndex] = useState<number | null>(null);
  const [hoveredPointIndex, setHoveredPointIndex] = useState<number | null>(
    null
  );
  const [dragging, setDragging] = useState<boolean>(false);
  const [draggedPointIndex, setDraggedPointIndex] = useState<number | null>(
    null
  );
  const [mousePosition, setMousePosition] = useState<[number, number] | null>(
    null
  );

  const padding = 20;

  const logicalWidth = imageWidth + padding * 2;
  const logicalHeight = imageHeight + padding * 2;

  // Convert display (scaled) coordinates to logical canvas coordinates
  const displayToLogical = (
    displayX: number,
    displayY: number,
    rect: DOMRect
  ): [number, number] => {
    const mouseX =
      (displayX * logicalWidth) / rect.width;
    const mouseY =
      (displayY * logicalHeight) / rect.height;
    return [mouseX, mouseY];
  };

  // Calculate distance between point and mouse
  const getDistance = (
    point: [number, number],
    mouse: [number, number]
  ): number => {
    return Math.hypot(point[0] - mouse[0], point[1] - mouse[1]);
  };

  // Check if mouse is near a line segment
  const isNearLine = (
    start: [number, number],
    end: [number, number],
    mouse: [number, number],
    threshold = 5
  ): boolean => {
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const lengthSquared = dx * dx + dy * dy;

    if (lengthSquared === 0) return getDistance(start, mouse) < threshold;

    const t = Math.max(
      0,
      Math.min(
        1,
        ((mouse[0] - start[0]) * dx + (mouse[1] - start[1]) * dy) /
          lengthSquared
      )
    );

    const closestX = start[0] + t * dx;
    const closestY = start[1] + t * dy;
    const distance = Math.hypot(mouse[0] - closestX, mouse[1] - closestY);

    return distance < threshold;
  };

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const redrawCanvas = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw all completed lines
      lines.forEach((line, index) => {
        const isSelected = selectedLineIndex === index;
        const isHovered = hoveredLineIndex === index;

        // Use line's own color, with overrides for selected/hovered states
        // Hover: #FF2400 (red)
        const displayColor = isHovered ? "#FF2400" : line.color;
        ctx.strokeStyle = displayColor;
        // Selected lines are thicker to make them more obvious
        ctx.lineWidth = isSelected ? 5 : isHovered ? 3 : 2;
        ctx.beginPath();
        ctx.moveTo(line.coordinates[0][0], line.coordinates[0][1]);
        ctx.lineTo(line.coordinates[1][0], line.coordinates[1][1]);
        ctx.stroke();

        // Draw arrow(s) in the center
        drawArrow(
          ctx,
          line.coordinates[0],
          line.coordinates[1],
          line.direction,
          displayColor
        );

        // Draw points only for selected line
        if (isSelected) {
          line.coordinates.forEach((point, pointIdx) => {
            const isPointHovered =
              hoveredPointIndex === index * 2 + pointIdx ||
              (dragging && draggedPointIndex === index * 2 + pointIdx);
            ctx.beginPath();
            const pointRadius = 7;
            ctx.arc(point[0], point[1], pointRadius, 0, Math.PI * 2);
            ctx.fillStyle = isPointHovered
              ? "#FF2400" // Red when point is hovered
              : line.color; // Line's own color
            ctx.fill();
          });
        }

        // Draw A and B labels on the sides of the line for selected line
        if (isSelected) {
          ctx.fillStyle = "#FF1744"; // Neon red
          ctx.font = "bold 16px Arial";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";

          // Add subtle drop shadow for better visibility
          ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
          ctx.shadowBlur = 3;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;

          // Calculate the center of the line
          const centerX = (line.coordinates[0][0] + line.coordinates[1][0]) / 2;
          const centerY = (line.coordinates[0][1] + line.coordinates[1][1]) / 2;

          // Calculate the line vector
          const dx = line.coordinates[1][0] - line.coordinates[0][0];
          const dy = line.coordinates[1][1] - line.coordinates[0][1];
          const lineLength = Math.sqrt(dx * dx + dy * dy);

          if (lineLength > 0) {
            // Calculate perpendicular vector (for side labels)
            const perpDx = -dy / lineLength;
            const perpDy = dx / lineLength;

            // Label offset from the line - increased to avoid overlapping with arrows
            const labelOffset = 40;

            // Going clockwise: point 1 -> side A (clockwise/right) -> point 2 -> side B (counter-clockwise/left)
            // Draw "A" on the clockwise/right side (when facing from point 1 to point 2)
            const sideAX = centerX - perpDx * labelOffset;
            const sideAY = centerY - perpDy * labelOffset;
            ctx.fillText("A", sideAX, sideAY);

            // Draw "B" on the counter-clockwise/left side (when facing from point 1 to point 2)
            const sideBX = centerX + perpDx * labelOffset;
            const sideBY = centerY + perpDy * labelOffset;
            ctx.fillText("B", sideBX, sideBY);
          }

          // Reset shadow for subsequent drawing
          ctx.shadowColor = "transparent";
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
        }
      });

      // Draw current line being drawn
      if (currentLine && isDrawing) {
        ctx.strokeStyle = currentLine.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(
          currentLine.coordinates[0][0],
          currentLine.coordinates[0][1]
        );
        if (mousePosition) {
          ctx.lineTo(mousePosition[0], mousePosition[1]);
        } else {
          ctx.lineTo(
            currentLine.coordinates[1][0],
            currentLine.coordinates[1][1]
          );
        }
        ctx.stroke();

        // Draw start point
        ctx.beginPath();
        ctx.arc(
          currentLine.coordinates[0][0],
          currentLine.coordinates[0][1],
          5,
          0,
          Math.PI * 2
        );
        ctx.fillStyle = currentLine.color;
        ctx.fill();
      }
    };

    // Draw the canvas
    redrawCanvas();
  }, [
    lines,
    currentLine,
    isDrawing,
    selectedLineIndex,
    hoveredLineIndex,
    hoveredPointIndex,
    dragging,
    draggedPointIndex,
    mousePosition,
    lineColor,
    imageWidth,
    imageHeight,
    imageURL,
  ]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const [mouseXDisplay, mouseYDisplay] = displayToLogical(
      e.clientX - rect.left,
      e.clientY - rect.top,
      rect
    );
    let mouseX = mouseXDisplay;
    let mouseY = mouseYDisplay;

    // Constrain to image bounds
    if (mouseX < padding) mouseX = padding;
    if (mouseX > imageWidth + padding) mouseX = imageWidth + padding;
    if (mouseY < padding) mouseY = padding;
    if (mouseY > imageHeight + padding) mouseY = imageHeight + padding;

    // Check if clicking on an existing point
    let clickedPointIndex: number | null = null;
    lines.forEach((line, lineIdx) => {
      if (getDistance(line.coordinates[0], [mouseX, mouseY]) < 5) {
        clickedPointIndex = lineIdx * 2;
        setSelectedLineIndex(lineIdx);
        setDragging(true);
        setDraggedPointIndex(clickedPointIndex);
        return;
      }
      if (getDistance(line.coordinates[1], [mouseX, mouseY]) < 5) {
        clickedPointIndex = lineIdx * 2 + 1;
        setSelectedLineIndex(lineIdx);
        setDragging(true);
        setDraggedPointIndex(clickedPointIndex);
        return;
      }
    });

    if (clickedPointIndex !== null) {
      return;
    }

    // Check if clicking on an existing line
    let clickedLineIndex: number | null = null;
    lines.forEach((line, lineIdx) => {
      if (
        isNearLine(line.coordinates[0], line.coordinates[1], [mouseX, mouseY])
      ) {
        clickedLineIndex = lineIdx;
        setSelectedLineIndex(lineIdx);
        return;
      }
    });

    if (clickedLineIndex !== null) {
      return;
    }

    // Start drawing a new line
    if (!isDrawing) {
      const cameraId = Number(getNumbersAfterEditCamera(location.href));
      const newLineColor = getNextLineColor(lines);
      // Only include metrics for active detection types
      const activeMetricIds = metricLabels
        .filter((m) => {
          if (m.label_name === "intruder_line_crossing") {
            return lineCrossingActive.intruder;
          }
          if (
            m.label_name === "vehicle_line_crossing" ||
            m.label_name === "bike_line_crossing"
          ) {
            return lineCrossingActive.vehicle;
          }
          return false;
        })
        .map((m) => m.id);

      setIsDrawing(true);
      setCurrentLine({
        // id is omitted for new lines - API will assign it
        camera: cameraId,
        metric_labels: activeMetricIds, // Only active detection types selected
        coordinates: [
          [mouseX, mouseY],
          [mouseX, mouseY],
        ],
        color: newLineColor,
        direction: "AtoB", // Default direction
      });
      setSelectedLineIndex(null);
    } else if (currentLine) {
      // Complete the line
      const completedLine = {
        ...currentLine,
        coordinates: [currentLine.coordinates[0], [mouseX, mouseY]] as [
          [number, number],
          [number, number]
        ],
      };
      const newLines = [...lines, completedLine];
      setLines(newLines);
      setCurrentLine(null);
      setIsDrawing(false);
      setMousePosition(null);
      // Select the newly created line (it's at the last index)
      setSelectedLineIndex(newLines.length - 1);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const [mouseXDisplay, mouseYDisplay] = displayToLogical(
      e.clientX - rect.left,
      e.clientY - rect.top,
      rect
    );
    let mouseX = mouseXDisplay;
    let mouseY = mouseYDisplay;

    // Constrain to image bounds
    if (mouseX < padding) mouseX = padding;
    if (mouseX > imageWidth + padding) mouseX = imageWidth + padding;
    if (mouseY < padding) mouseY = padding;
    if (mouseY > imageHeight + padding) mouseY = imageHeight + padding;

    if (dragging && draggedPointIndex !== null && selectedLineIndex !== null) {
      // Update the dragged point - create new line object to avoid mutating original
      const updatedLines = lines.map((line, index) => {
        if (index !== selectedLineIndex) return line;

        // Create new line with updated coordinates
        const newCoordinates: [[number, number], [number, number]] =
          draggedPointIndex % 2 === 0
            ? [[mouseX, mouseY], line.coordinates[1]]
            : [line.coordinates[0], [mouseX, mouseY]];

        return { ...line, coordinates: newCoordinates };
      });
      setLines(updatedLines);
      setEditsMade(true);
    } else if (isDrawing && currentLine) {
      // Update preview line
      setMousePosition([mouseX, mouseY]);
    } else {
      // Check for hover states
      let foundHover = false;

      // Check points first
      lines.forEach((line, lineIdx) => {
        if (getDistance(line.coordinates[0], [mouseX, mouseY]) < 5) {
          setHoveredPointIndex(lineIdx * 2);
          setHoveredLineIndex(null);
          foundHover = true;
          return;
        }
        if (getDistance(line.coordinates[1], [mouseX, mouseY]) < 5) {
          setHoveredPointIndex(lineIdx * 2 + 1);
          setHoveredLineIndex(null);
          foundHover = true;
          return;
        }
      });

      if (!foundHover) {
        setHoveredPointIndex(null);

        // Check lines
        let hoveredLine: number | null = null;
        lines.forEach((line, lineIdx) => {
          if (
            isNearLine(line.coordinates[0], line.coordinates[1], [
              mouseX,
              mouseY,
            ])
          ) {
            hoveredLine = lineIdx;
          }
        });
        setHoveredLineIndex(hoveredLine);
      }
    }
  };

  const handleMouseUp = () => {
    if (dragging) {
      setDragging(false);
      setDraggedPointIndex(null);
    }
  };

  // Handle delete key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Backspace" || event.key === "Delete") {
        if (selectedLineIndex !== null) {
          const updatedLines = lines.filter(
            (_, index) => index !== selectedLineIndex
          );
          setLines(updatedLines);
          setSelectedLineIndex(null);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedLineIndex, lines, setLines, setSelectedLineIndex]);

  const cursorType =
    dragging || hoveredPointIndex !== null || hoveredLineIndex !== null
      ? "pointer"
      : "crosshair";

  const drawingArea = (
    <div
      style={{
        position: "relative",
        width: logicalWidth,
        height: logicalHeight,
        border: "2px solid black",
      }}
    >
      {/* Background image */}
      <img
        src={imageURL}
        alt="Camera Preview"
        style={{
          position: "absolute",
          top: padding,
          left: padding,
          width: imageWidth,
          height: imageHeight,
          pointerEvents: "none",
        }}
      />
      {/* Canvas overlay for drawing lines */}
      <canvas
        ref={canvasRef}
        width={logicalWidth}
        height={logicalHeight}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          cursor: cursorType,
          background: "transparent",
        }}
      />
    </div>
  );

  return (
    <div>
      {setLargerMode && (
        <div className="font-mulish">
          <FormControlLabel
            sx={{ fontFamily: "Mulish" }}
            control={
              <Switch
                color="warning"
                checked={largerMode}
                disabled={false}
                onChange={(event) => {
                  setLargerMode(event.target.checked);
                }}
              />
            }
            label="Larger Mode"
          />
        </div>
      )}
      {setLargerMode && <div style={{ marginBottom: "10px" }} />}
      <div
        style={{
          position: "relative",
          width: largerMode ? logicalWidth * LARGER_MODE_SCALE : logicalWidth,
          height: largerMode ? logicalHeight * LARGER_MODE_SCALE : logicalHeight,
        }}
      >
        <div
          style={{
            transform: largerMode
              ? `scale(${LARGER_MODE_SCALE})`
              : "none",
            transformOrigin: "top left",
            width: logicalWidth,
            height: logicalHeight,
          }}
        >
          {drawingArea}
        </div>
      </div>
    </div>
  );
};
